import React, { useState } from "react";
import AuthLayout from "../../layout/AuthLayout";
import {
  Backdrop,
  Box,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  CustomInput,
  CustomPasswordInput,
} from "../../components/Common/CustomInputs";
import { EmailOutlined, LoginOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { CustomBtn } from "../../components/Common/CustomBtn";
import axiosInstance from "../../utils/config/config";
import { ErrorSnackbar } from "../../components/Common/CustomSnackbar";
import { useDispatch } from "react-redux";
import {
  clearUser,
  getIPHCPractioner,
  getPractioner,
  getUserById,
  getPatient,
} from "../../redux/actions/user.dispatcher";
import TwoFactorVerify from "./TwoFactorVerify";

const Login = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorUserId, setTwoFactorUserId] = useState(null);

  React.useEffect(() => {
    if (!localStorage.getItem("token")) {
      dispatch(clearUser());
    }
  }, [dispatch]);

  const mediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState({
    error: false,
    errorMsg: "",
  });

  const handleLogin = async () => {
    setErrorState({ error: false, errorMsg: "" });
    if (!email || !password) {
      setErrorState({
        error: true,
        errorMsg: "Please fill all the fields",
      });
      return;
    }
    try {
      setLoading(true);
      const response = await axiosInstance.post("/user/login", {
        email: email.trim().toLowerCase(),
        password,
      });
      if (response) {
        // Check if 2FA is required
        if (response.data.requires2FA) {
          setTwoFactorUserId(response.data.userId);
          setRequires2FA(true);
          return;
        }

        localStorage.setItem("token", response.data.accessToken);
        await Promise.all([
          dispatch(getUserById()),
          dispatch(getPractioner()),
          dispatch(getIPHCPractioner()),
          dispatch(getPatient(response?.data?.user?.id)),
        ]);

        // Redirect to password change page if the user must change their password
        if (response.data.user?.mustChangePassword) {
          navigate("/change-password");
        } else {
          navigate("/home");
        }
      }
    } catch (error) {
      setErrorState({
        error: true,
        errorMsg: error.response?.data?.message || "Login failed",
      });
    } finally {
      setLoading(false);
    }
  };

  const handle2FASuccess = async (responseData) => {
    localStorage.setItem("token", responseData.accessToken);
    await Promise.all([
      dispatch(getUserById()),
      dispatch(getPractioner()),
      dispatch(getIPHCPractioner()),
      dispatch(getPatient(responseData?.user?.id)),
    ]);

    // Redirect to password change page if the user must change their password
    if (responseData?.user?.mustChangePassword) {
      navigate("/change-password");
    } else {
      navigate("/home");
    }
  };

  const handleBack = () => {
    setRequires2FA(false);
    setTwoFactorUserId(null);
  };

  // Show 2FA verification screen if required
  if (requires2FA) {
    return (
      <TwoFactorVerify
        userId={twoFactorUserId}
        email={email.trim().toLowerCase()}
        password={password}
        onSuccess={handle2FASuccess}
        onBack={handleBack}
      />
    );
  }

  return (
    <AuthLayout>
      <Box sx={{ px: 4 }} onKeyDown={(e) => e.key === "Enter" && handleLogin()}>
        <Typography
          sx={{
            fontFamily: "'Gotham', sans-serif",
            fontSize: "50px",
            color: theme.palette.text.primary,
          }}
        >
          Login
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Quicksand', sans-serif",
            fontSize: "22px",
            color: theme.palette.text.primary,
          }}
        >
          Welcome to the AEG Dashboard
        </Typography>

        <Box sx={{ my: 5, width: "100%" }}>
          <CustomInput
            placeholder="Email"
            value={email}
            setValue={setEmail}
            Icon={EmailOutlined}
            rightIcon={false}
            width={"100%"}
            color={theme.palette.text.primary}
          />
          <CustomPasswordInput
            placeholder="Password"
            value={password}
            setValue={setPassword}
            width={"100%"}
          />

          <Typography
            onClick={() => navigate("/forgot-password")}
            sx={{
              fontFamily: "'Quicksand', sans-serif",
              fontSize: "16px",
              color: theme.palette.text.primary,
              textDecorationLine: "underline",
              cursor: "pointer",
            }}
          >
            Forgot password?
          </Typography>
        </Box>

        <CustomBtn
          text="Login"
          Icon={LoginOutlined}
          width={mediumScreen ? "50%" : "30%"}
          onClick={handleLogin}
        />
      </Box>
      <Box sx={{ px: 4 }}>
        <Typography
          onClick={() => navigate("/register")}
          sx={{
            fontSize: "18px",
            fontFamily: "'Quicksand', sans-serif",
            color: theme.palette.text.primary,
            fontWeight: "400",
          }}
        >
          If you are a new user, then you will need to register here. This login
          is different from our Learning Management System.{" "}
          <Typography
            component={"span"}
            sx={{
              fontSize: "18px",
              fontFamily: "'Quicksand', sans-serif",
              color: theme.palette.text.primary,
              fontWeight: "600",
              display: "inline",
              cursor: "pointer",
              textDecorationLine: "underline",
            }}
          >
            Register
          </Typography>
        </Typography>
      </Box>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <ErrorSnackbar
        error={errorState.error}
        handleCloseError={() => setErrorState({ error: false, errorMsg: "" })}
        errorMsg={errorState.errorMsg}
      />
    </AuthLayout>
  );
};

export default Login;
